import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Menu } from './menu.model';

export class RenderableMenu {
  private readonly _children$ = new BehaviorSubject<RenderableMenu[]>([]);
  private readonly _isMatch$: Observable<boolean>;
  private readonly _isActive$: Observable<boolean>;

  constructor(
    private readonly menu: Menu,
    private readonly parentUrl: string | null,
    currentUrl$: Observable<string>
  ) {
    this._isMatch$ = currentUrl$.pipe(map((currentUrl) => RenderableMenu.isMatchedUrl(this.url, currentUrl)));
    this._isActive$ = currentUrl$.pipe(map((currentUrl) => RenderableMenu.isActiveUrl(this.url, currentUrl)));
  }

  get hidden(): Observable<boolean> {
    return this.menu.hidden ?? of(false);
  }

  get title(): string {
    return this.menu.title;
  }

  public loadChildren(children: RenderableMenu[]) {
    this._children$.next(children || []);
  }

  get url(): string {
    return RenderableMenu.joinUrl(this.parentUrl, this.menu.routerLink);
  }

  get children$(): Observable<RenderableMenu[]> {
    return this._children$.asObservable();
  }

  get isMatched$(): Observable<boolean> {
    return this._isMatch$;
  }

  get isActive$(): Observable<boolean> {
    return this._isActive$;
  }

  private static joinUrl(parentUrl: string | null, routeSegment: string): string {
    const child = (routeSegment || '').trim();
    if (child.startsWith('/')) {
      return RenderableMenu.normalizePath(child);
    }

    const base = parentUrl ? RenderableMenu.normalizePath(parentUrl) : '';
    const combined = `${base}/${child}`;
    return RenderableMenu.normalizePath(combined);
  }

  private static normalizePath(url: string): string {
    // Drop query params + fragments, normalize slashes, and drop trailing slashes (except root).
    const pathOnly = (url || '').split(/[?#]/)[0] ?? '';
    const withSingleSlashes = pathOnly.replace(/\/{2,}/g, '/');
    const withLeadingSlash = withSingleSlashes.startsWith('/') ? withSingleSlashes : `/${withSingleSlashes}`;
    const withoutTrailingSlash = withLeadingSlash.replace(/\/+$/, '');
    return withoutTrailingSlash.length === 0 ? '/' : withoutTrailingSlash;
  }

  private static isActiveUrl(menuUrl: string, currentUrl: string): boolean {
    return RenderableMenu.normalizePath(menuUrl) === RenderableMenu.normalizePath(currentUrl);
  }

  private static isMatchedUrl(menuUrl: string, currentUrl: string): boolean {
    const menuPath = RenderableMenu.normalizePath(menuUrl);
    const currentPath = RenderableMenu.normalizePath(currentUrl);

    if (menuPath === '/') {
      return currentPath === '/';
    }

    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`);
  }
}
